import {
  createStyles,
  Flex,
  Group,
  Menu,
  ScrollArea,
  Text as MantineText, // eslint-disable-line no-restricted-imports
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from 'react-responsive';
import { type ReactNode, useState } from 'react';
import { IconChevronDown, IconEdit, IconOutbound, IconPencil } from '@tabler/icons-react';

import { UnstyledNextLink } from '~/components/shared/UnstyledNextLink/UnstyledNextLink';
import { Text } from '~/domains/design-system/Text';
import dt from '~/testing';

import { EntryWithdrawModal } from '../Modal/EntryWithdrawModal';

const useStyles = createStyles((theme) => ({
  target: {
    display: 'flex',
    cursor: 'pointer',
    gap: theme.other.spacing._4,
    justifyContent: 'space-between',
    flexGrow: 1,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  bottomDivider: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.colors.gray[2]}`,
      paddingBottom: `${theme.spacing.md}px`,
      marginBottom: `${theme.spacing.md}px`,
    },
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  orderNumber: {
    color: theme.colors.gray[6],
    fontSize: '0.875rem',
  },
}));

type EntryRowProps = {
  children: ReactNode;
  entryId: string;
  name: string;
  entryOrder?: number;
  contestId: string;
  options?: {
    editName?: {
      title: string;
      onClick: () => void;
      isHidden?: boolean;
    };
    edit?: {
      title: string;
      href?: string;
      isHidden?: boolean;
    };
    withdrawal?: {
      title: string;
      isHidden?: boolean;
    };
  };
  isEntertainmentOnly?: boolean;
};

function EntryRow({
  entryId,
  name,
  entryOrder,
  children,
  contestId,
  options,
  isEntertainmentOnly,
}: EntryRowProps) {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.lg });

  const [isEntryWithdrawModalOpened, setIsEntryWithdrawModalOpened] = useState(false);

  const { edit, editName, withdrawal } = options || {};
  const visibleEdit = edit?.isHidden ? undefined : edit;
  const visibleEditName = editName?.isHidden ? undefined : editName;
  const visibleWithdrawal = withdrawal?.isHidden ? undefined : withdrawal;
  const hasVisibleAction = !!(visibleEdit || visibleEditName || visibleWithdrawal);

  return (
    <Flex
      direction={isTablet ? 'column' : 'row'}
      justify="space-between"
      align="stretch"
      gap={theme.spacing.xl}
      w="100%"
      className={classes.bottomDivider}
      data-test-id={dt.contestDetail.section.myEntries.row.container}
    >
      <Flex justify="space-between" align="center" w="100%" maw={84}>
        {hasVisibleAction ? (
          <Menu shadow="md" width={200} position="bottom-start">
            <Menu.Target>
              <MantineText
                className={classes.target}
                size={14}
                weight={700}
                data-test-id={dt.contestDetail.section.myEntries.row.title}
              >
                <div className={classes.nameContainer}>
                  {name}
                  {entryOrder && (
                    <Text variant="body-small" className={classes.orderNumber}>
                      {entryOrder}
                    </Text>
                  )}
                </div>
                <IconChevronDown size={20} />
              </MantineText>
            </Menu.Target>

            <Menu.Dropdown
              data-test-id={dt.contestDetail.section.myEntries.row.options.container}
            >
              {visibleEdit && (
                <UnstyledNextLink
                  href={visibleEdit.href}
                  data-test-id={dt.contestDetail.section.myEntries.row.options.edit.link}
                >
                  <Menu.Item
                    icon={<IconEdit size={theme.spacing.lg} />}
                    data-test-id={dt.contestDetail.section.myEntries.row.options.edit.text}
                  >
                    {visibleEdit.title}
                  </Menu.Item>
                </UnstyledNextLink>
              )}
              {visibleEditName && (
                <Menu.Item
                  icon={<IconPencil size={theme.spacing.lg} />}
                  data-test-id={dt.contestDetail.section.myEntries.row.options.withdrawal.text}
                  onClick={visibleEditName.onClick}
                >
                  {visibleEditName.title}
                </Menu.Item>
              )}
              {visibleWithdrawal && (
                <Menu.Item
                  icon={<IconOutbound size={theme.spacing.lg} />}
                  data-test-id={dt.contestDetail.section.myEntries.row.options.withdrawal.text}
                  onClick={() => setIsEntryWithdrawModalOpened(true)}
                >
                  {visibleWithdrawal.title}
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        ) : (
          <MantineText
            size={14}
            weight={700}
            data-test-id={dt.contestDetail.section.myEntries.row.title}
          >
            <div className={classes.nameContainer}>
              {name}
              {entryOrder && (
                <Text variant="body-small" className={classes.orderNumber}>
                  {entryOrder}
                </Text>
              )}
            </div>
          </MantineText>
        )}
      </Flex>
      <ScrollArea type="never" mx={`-${theme.spacing.lg}px`}>
        <Group
          noWrap
          px={theme.spacing.lg}
          spacing={theme.other.spacing._4}
          data-test-id={dt.contestDetail.section.myEntries.row.items}
        >
          {children}
        </Group>
      </ScrollArea>
      <EntryWithdrawModal
        entryId={entryId}
        contestId={contestId}
        isEntertainmentOnly={isEntertainmentOnly}
        isOpen={isEntryWithdrawModalOpened}
        setModal={setIsEntryWithdrawModalOpened}
      />
    </Flex>
  );
}

export default EntryRow;
